// Shared visual kit for the district-side GOV interface.
// Behance-inspired premium SaaS: calm, breathable, disciplined Azure accents,
// invisible structural containers, large touch targets, mobile-first at 390px.
import { CheckCircle, WarningCircle, Circle, CircleNotch, CaretRight, PaperPlaneTilt, EnvelopeSimple, XCircle } from "@phosphor-icons/react";

// "Purple" premium SaaS palette — violet primary, quiet grey canvas, gradient metric cards.
export const C = {
  indigo: "#7C4DFF", indigoDark: "#5B34D6", indigoSoft: "#F1ECFF", indigoText: "#6A3EE8",
  azure: "#7C4DFF", azureDark: "#5B34D6", azureSoft: "#F1ECFF",
  royal: "#7C4DFF", gold: "#F7A81B", goldInk: "#4A3200",
  green: "#22B07D", amber: "#F5A524", cranberry: "#F0577D", turquoise: "#00A2BE", aqua: "#00A2BE",
  ink: "#1B1B2F", sub: "#54565A", disabled: "#8A8FA3", line: "#ECECF3", bg: "#F5F6FB",
};

// Vibrant gradients for top summary / metric cards (pink→coral, blue→azure, teal→green).
export const GRAD = {
  coral: "linear-gradient(135deg,#FBAB7E 0%,#F7719F 100%)",
  blue: "linear-gradient(135deg,#74B6F2 0%,#4A82E0 100%)",
  teal: "linear-gradient(135deg,#5AD9B0 0%,#37BE96 100%)",
  violet: "linear-gradient(120deg,#7C4DFF 0%,#9B5CF6 55%,#B44BE0 100%)",
};

export function Page({ title, subtitle, action, children }) {
  return (
    <div className="mx-auto w-full max-w-3xl px-1">
      <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-[30px] font-medium leading-[1.1] tracking-tight" style={{ color: C.ink }}>{title}</h1>
          {subtitle && <p className="mt-2 text-[16px] leading-relaxed" style={{ color: C.sub }}>{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="mt-7 space-y-5">{children}</div>
    </div>
  );
}

// Shared violet hero banner used across every primary screen for a uniform look.
export function PageHero({ title, subtitle, stats, right }) {
  return (
    <div className="relative overflow-hidden rounded-2xl px-7 py-8 text-white md:px-9 md:py-9" style={{ background: GRAD.violet }}>
      <div className="pointer-events-none absolute -right-12 -top-20 h-64 w-64 rounded-full" style={{ background: "rgba(255,255,255,0.10)" }} />
      <div className="pointer-events-none absolute -bottom-24 right-24 h-52 w-52 rounded-full" style={{ background: "rgba(255,255,255,0.07)" }} />
      <div className="relative flex flex-col gap-7 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-xl">
          <img src={`${process.env.PUBLIC_URL || ""}/rotary-reverse.png`} alt="Rotary" className="mb-5 h-8 w-auto object-contain" />
          <h1 className="text-[28px] font-bold leading-tight tracking-tight md:text-[34px]">{title}</h1>
          {subtitle && <p className="mt-2 max-w-md text-[16px] font-medium text-white/85">{subtitle}</p>}
        </div>
        {right ? (
          <div className="w-full shrink-0 lg:w-auto lg:min-w-[260px]">{right}</div>
        ) : stats && stats.length > 0 ? (
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl bg-white/12 px-4 py-3 backdrop-blur-sm">
                <p className="text-[30px] font-bold leading-none">{s.value}</p>
                <p className="mt-2 text-[13px] font-semibold uppercase tracking-wider text-white/75">{s.label}</p>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function Card({ children, className = "", pad = true, ...rest }) {
  return (
    <section {...rest}
      className={`rounded-2xl border border-[#D5D9E6] bg-white ${pad ? "p-6" : ""} ${className}`}
      style={{ boxShadow: "0 6px 24px rgba(28,42,58,0.10)", ...(rest.style || {}) }}>
      {children}
    </section>
  );
}

export function Overline({ children, className = "" }) {
  return <p className={`text-[13px] font-semibold uppercase tracking-[0.12em] ${className}`} style={{ color: C.sub }}>{children}</p>;
}

export function SectionTitle({ icon: Icon, title, sub, right }) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        {Icon && (
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl" style={{ background: C.azureSoft, color: C.azure }}>
            <Icon size={20} weight="bold" />
          </span>
        )}
        <div>
          <h2 className="font-display text-[18px] font-medium tracking-tight" style={{ color: C.ink }}>{title}</h2>
          {sub && <p className="mt-0.5 text-[14px]" style={{ color: C.sub }}>{sub}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}

export function PrimaryButton({ children, gold = false, className = "", ...rest }) {
  return (
    <button {...rest}
      className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-6 text-[16px] font-medium text-white transition-all duration-200 active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100 ${className}`}
      style={{ background: gold ? C.gold : C.azure, color: gold ? C.goldInk : "#fff" }}>
      {children}
    </button>
  );
}

export function GhostButton({ children, className = "", ...rest }) {
  return (
    <button {...rest}
      className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-5 text-[16px] font-medium transition-all duration-200 active:scale-[0.97] disabled:opacity-50 ${className}`}
      style={{ color: C.azure, background: "transparent", boxShadow: "inset 0 0 0 1.5px " + C.line }}>
      {children}
    </button>
  );
}

const PILL = {
  // GOV visit lifecycle
  not_initiated: { label: "Not initiated", bg: "#EEF1F6", fg: "#5B6B7F", icon: Circle },
  in_progress: { label: "In progress", bg: "#FFF4E5", fg: C.amber, icon: WarningCircle },
  completed: { label: "Completed", bg: "#E8F5E9", fg: C.green, icon: CheckCircle },
  // review states
  ok: { label: "OK", bg: "#E8F5E9", fg: C.green, icon: CheckCircle },
  accepted: { label: "Accepted", bg: "#E8F5E9", fg: C.green, icon: CheckCircle },
  attention: { label: "Needs improvement", bg: "#FDECEA", fg: C.cranberry, icon: WarningCircle },
  missing: { label: "Missing", bg: "#FDECEA", fg: C.cranberry, icon: Circle },
  submitted: { label: "Submitted", bg: C.azureSoft, fg: C.azure, icon: CheckCircle },
  awaiting: { label: "Awaiting review", bg: C.azureSoft, fg: C.azure, icon: Circle },
  resubmitted: { label: "Resubmitted", bg: "#FFF4E5", fg: C.amber, icon: WarningCircle },
  optional: { label: "Optional", bg: "#EEF1F6", fg: "#8A97A8", icon: Circle },
  // email delivery
  sent: { label: "Sent", bg: C.azureSoft, fg: C.azure, icon: PaperPlaneTilt },
  delivered: { label: "Delivered", bg: "#E8F5E9", fg: C.green, icon: CheckCircle },
  bounced: { label: "Bounced", bg: "#FDECEA", fg: C.cranberry, icon: XCircle },
  failed: { label: "Failed", bg: "#FDECEA", fg: C.cranberry, icon: XCircle },
  complained: { label: "Complained", bg: "#FDECEA", fg: C.cranberry, icon: WarningCircle },
  delayed: { label: "Delayed", bg: "#FFF4E5", fg: C.amber, icon: WarningCircle },
  pending: { label: "Pending", bg: "#EEF1F6", fg: "#8A97A8", icon: EnvelopeSimple },
};

export function StatusPill({ status, children }) {
  const s = PILL[status] || PILL.optional;
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[13px] font-medium" style={{ background: s.bg, color: s.fg }}>
      <Icon size={13} weight="fill" /> {children || s.label}
    </span>
  );
}

export function Loader({ label = "loading…" }) {
  return <div className="flex items-center gap-2 py-6 text-[15px]" style={{ color: C.sub }}><CircleNotch size={18} weight="bold" className="animate-spin" style={{ color: C.azure }} /> {label}</div>;
}

export function FunctionCard({ icon: Icon, tint, title, desc, onClick, cta, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="group w-full text-left">
      <Card className="flex h-full items-center gap-4 transition-all duration-200 group-hover:-translate-y-0.5">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl" style={{ background: (tint || C.azure) + "16", color: tint || C.azure }}>
          <Icon size={26} weight="bold" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-display text-[17px] font-medium tracking-tight" style={{ color: C.ink }}>{title}</span>
          <span className="mt-0.5 block text-[14px] leading-relaxed" style={{ color: C.sub }}>{desc}</span>
        </span>
        <CaretRight size={18} weight="bold" className="shrink-0 transition-transform group-hover:translate-x-0.5" style={{ color: C.sub }} />
      </Card>
    </button>
  );
}

// Clean list row used for clubs, districts and users (avoids AI-slop card grids).
export function Row({ children, className = "", ...rest }) {
  return (
    <button {...rest}
      className={`flex w-full items-center gap-3.5 px-5 text-left transition-colors hover:bg-[#F7FAFF] active:bg-[#EEF4FF] ${className}`}
      style={{ minHeight: 64 }}>
      {children}
    </button>
  );
}

// Circular avatar/initial badge for list rows.
export function Avatar({ icon: Icon, label, tint }) {
  const bg = (tint || C.azure) + "16";
  return (
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-[15px] font-medium" style={{ background: bg, color: tint || C.azure }}>
      {Icon ? <Icon size={19} weight="bold" /> : (label || "?").slice(0, 1).toUpperCase()}
    </span>
  );
}
